import { useState } from "react";
import { T } from "@/components/T";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Divider } from "@/components/MotifBackground";
import { resendVerification } from "@/lib/api";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const redirectUri = window.location.origin + "/auth/google";
  const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
  sessionStorage.setItem("g_oauth_state", state);
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid email profile",
    access_type: "online",
    include_granted_scopes: "true",
    prompt: "select_account",
    state,
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

const inputCls = "w-full border border-line bg-parchment px-4 py-2.5 text-ink focus:border-saffron focus:outline-none";

// Password input with a show/hide toggle. Reused across Login, Register and Reset.
export function PasswordField({ value, onChange, testId, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testId}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        className={`${inputCls} pr-11`}
        {...props}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={testId ? `${testId}-toggle` : "password-toggle"}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-3 transition-colors hover:text-saffron"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="mx-auto max-w-md px-5 py-16 md:py-24">
      <div className="border border-line bg-surface p-8 md:p-10">
        <div className="mb-8 text-center">
          <Link to="/" className="font-deva text-4xl text-ink">धर्मार्थ</Link>
          <p className="mt-3 font-serif-latin text-2xl text-ink">{title}</p>
          {subtitle && <p className="mt-1 text-sm text-ink-3">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

export function GoogleButton() {
  return (
    <button type="button" onClick={googleLogin} data-testid="google-login-button" className="flex w-full items-center justify-center gap-3 border border-line bg-parchment py-3 font-medium text-ink transition-colors hover:bg-surface-alt">
      <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75z"/></svg>
      <T>Continue with Google</T>
    </button>
  );
}

export default function Login() {
  const { user, ready, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [needVerify, setNeedVerify] = useState(false);
  const [resent, setResent] = useState(false);

  if (ready && user) return <Navigate to={user.role === "admin" ? "/admin" : "/account"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedVerify(false);
    setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      const status = err.response?.status;
      const detail = formatError(err.response?.data?.detail) || err.message;
      setError(detail);
      if (status === 403 && /confirm your email/i.test(detail)) setNeedVerify(true);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try { await resendVerification(email); } catch { /* generic */ }
    setResent(true);
  };

  return (
    <AuthShell title="Sign in" subtitle="Access bookings and saved knowledge">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm text-ink-2"><T>Email</T></label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1.5 block text-sm text-ink-2"><T>Password</T></label>
          <PasswordField testId="login-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <p data-testid="login-error" className="text-sm text-[var(--saffron-dark)]">{error}</p>}
        {needVerify && (
          resent
            ? <p className="text-sm text-ink-2" data-testid="login-resent"><T>Verification email sent — please check your inbox.</T></p>
            : <button type="button" onClick={resend} data-testid="login-resend-verify" className="text-sm text-saffron link-underline"><T>Resend verification email</T></button>
        )}
        <button data-testid="login-submit" disabled={busy} className="w-full bg-[var(--saffron)] py-3 font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <p className="text-center text-sm">
          <Link to="/forgot-password" data-testid="forgot-password-link" className="text-ink-3 hover:text-saffron link-underline"><T>Forgot password?</T></Link>
        </p>
      </form>
      <div className="my-6"><Divider /></div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-ink-3">
        <T>New here?</T> <Link to="/register" className="text-saffron link-underline"><T>Create an account</T></Link>
      </p>
    </AuthShell>
  );
}
